import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Card, CardHeader, CardActions, CardContent, Typography, Box, LinearProgress, Checkbox } from '@material-ui/core';
import { RolesTypes } from '../../../../constants/roles';

const useStyles = makeStyles(theme => ({
  cardRoot: {
    minHeight: 320,
    borderRadius: 6,
    height: '100%',
    position: 'relative',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    borderBottom: `solid 1px ${theme.palette.common.gamsOrange}`,
    paddingBottom: 8,
  },
  cardTitleNs: {
    color: theme.palette.grey[400],
    borderBottom: `solid 1px ${theme.palette.grey[400]}`,
  },
  cardTitleStatus: {
    color: theme.palette.grey[400],
  },
  cardContent: {
    paddingTop: theme.spacing(1.6),
  },
  cardAction: {
    backgroundColor: theme.palette.grey[200],
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.3, 2, 2.1, 2),
  },
  indicatorRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  checkbox: {
    padding: '0 6px 0 0',
    color: theme.palette.common.gamsDarkBlue,
    '&.Mui-checked': {
      color: theme.palette.common.gamsDarkBlue,
    },
  },
  indicatorContent: {
    flex: 1,
    minWidth: 0,
  },
  indicatorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  indicatorTitle: {
    lineHeight: 1.3,
    flex: 1,
    paddingRight: theme.spacing(1),
  },
  indicatorTitleNs: {
    color: theme.palette.grey[400],
  },
  indicatorPercent: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.common.gamsDarkBlue,
    whiteSpace: 'nowrap',
  },
  indicatorPercentNs: {
    color: theme.palette.grey[400],
  },
  skillPrgRoot: {
    height: 8,
  },
  skillPrgColorPrimary: {
    backgroundColor: theme.palette.grey[300],
  },
  skillPrgBar: {
    backgroundColor: theme.palette.common.gamsDarkBlue,
  },
  skillPrgBarNs: {
    backgroundColor: theme.palette.grey[400],
  },
  skillPrgBarPs: {
    backgroundColor: theme.palette.common.blue ? theme.palette.common.blue[500] : theme.palette.primary.main,
  },
  overallTitle: {
    lineHeight: 1.3,
    marginBottom: 2,
  },
  overallTitleNs: {
    color: theme.palette.grey[400],
  },
  overallPrgRoot: {
    height: 8,
  },
  overallPrgColorPrimary: {
    backgroundColor: theme.palette.grey[300],
  },
  overallPrgBar: {
    backgroundColor: theme.palette.common.gamsOrange,
  },
  overallPrgBarNs: {
    backgroundColor: theme.palette.grey[400],
  },
  overallPrgBarPs: {
    backgroundColor: theme.palette.common.orange ? theme.palette.common.orange[500] : theme.palette.warning.main,
  },
}));

const QscIndicators = ({ scores, userRole }) => {
  const classes = useStyles();

  const canToggle = userRole === RolesTypes.COACH || userRole === RolesTypes.SUPERUSER;

  // Each card manages its own checked set independently
  return (
    <Grid container justifyContent="center" alignItems="center" spacing={3}>
      {scores.map((score, index) => (
        <QscIndicatorCard
          key={index}
          score={score}
          canToggle={canToggle}
          classes={classes}
        />
      ))}
    </Grid>
  );
};

const QscIndicatorCard = ({ score, canToggle, classes }) => {
  const allIds = useMemo(
    () => new Set(score.learningTopicScores.map((_, i) => i)),
    [score.learningTopicScores]
  );
  const [checkedIds, setCheckedIds] = useState(allIds);

  const handleToggle = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { overallScore, overallObtainableScore } = useMemo(() => {
    let totalScore = 0;
    let totalObtainable = 0;
    score.learningTopicScores.forEach((topic, i) => {
      if (checkedIds.has(i)) {
        totalScore += topic.score;
        totalObtainable += topic.obtainableScore;
      }
    });
    return { overallScore: totalScore, overallObtainableScore: totalObtainable };
  }, [checkedIds, score.learningTopicScores]);

  const normaliseOverall = overallObtainableScore > 0
    ? Math.round((overallScore / overallObtainableScore) * 100)
    : 0;

  const isNotStarted = score.status === 'not started';
  const isInProgress = score.status === 'in progress';
  const statusCaption = score.status === 'finished'
    ? null
    : <Typography variant="body2" component="span" className={classes.cardTitleStatus}>({score.status})</Typography>;

  return (
    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
      <Card className={classes.cardRoot} variant="outlined">
        <CardHeader
          title={
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>{score.challengeLearningTopic}</Box>
              <Box textAlign="right">{statusCaption}</Box>
            </Box>
          }
          titleTypographyProps={{ variant: 'h3', component: 'h3' }}
          classes={{
            root: classes.cardHeader,
            title: `${classes.cardTitle} ${isNotStarted ? classes.cardTitleNs : ''}`,
          }}
        />
        <CardContent className={classes.cardContent}>
          {score.learningTopicScores.map((topic, i) => {
            const indicatorPercent = topic.obtainableScore > 0
              ? Math.round((topic.score / topic.obtainableScore) * 100)
              : 0;
            const normaliseIndicator = topic.obtainableScore > 0
              ? (topic.score / topic.obtainableScore) * 100
              : 0;
            const isChecked = checkedIds.has(i);

            return (
              <div key={i} className={classes.indicatorRow}>
                {canToggle && (
                  <Checkbox
                    className={classes.checkbox}
                    checked={isChecked}
                    onChange={() => handleToggle(i)}
                    size="small"
                    disableRipple
                  />
                )}
                <div className={classes.indicatorContent}>
                  <div className={classes.indicatorHeader}>
                    <Typography
                      variant="body2"
                      component="h4"
                      className={`${classes.indicatorTitle} ${isNotStarted ? classes.indicatorTitleNs : ''}`}
                    >
                      {topic.indicator.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="span"
                      className={`${classes.indicatorPercent} ${isNotStarted ? classes.indicatorPercentNs : ''}`}
                    >
                      {indicatorPercent}%
                    </Typography>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={normaliseIndicator}
                    classes={{
                      root: classes.skillPrgRoot,
                      colorPrimary: classes.skillPrgColorPrimary,
                      bar: `${classes.skillPrgBar} ${isNotStarted ? classes.skillPrgBarNs : ''} ${isInProgress ? classes.skillPrgBarPs : ''}`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
        <CardActions className={classes.cardAction}>
          <Box width="100%">
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="body1"
                component="h4"
                className={`${classes.overallTitle} ${isNotStarted ? classes.overallTitleNs : ''}`}
              >
                Overall
              </Typography>
              <Typography
                variant="body1"
                component="div"
                className={`${classes.overallTitle} ${isNotStarted ? classes.overallTitleNs : ''}`}
              >
                {normaliseOverall}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={normaliseOverall}
              classes={{
                root: classes.overallPrgRoot,
                colorPrimary: classes.overallPrgColorPrimary,
                bar: `${classes.overallPrgBar} ${isNotStarted ? classes.overallPrgBarNs : ''} ${isInProgress ? classes.overallPrgBarPs : ''}`,
              }}
            />
          </Box>
        </CardActions>
      </Card>
    </Grid>
  );
};

export default QscIndicators;
